/**
 * Staff API Composable
 * Handles API calls for staff authentication endpoints
 */

import http from '@/services/http'
import type { AxiosResponse } from 'axios'
import type { ApiResponse } from '@/types'
import type { StaffSession, SessionCheck } from '@/stores/types/staff.types'

/**
 * Unwrap axios response to get just the data
 */
const unwrap = <T>(p: Promise<AxiosResponse<T>>): Promise<T> =>
  p.then((r) => r.data)

/**
 * Staff authentication API methods
 */
export function useStaffApi() {
  /**
   * Get current staff session information
   * GET /v1/admin/auth/me
   */
  async function getSession(): Promise<StaffSession | null> {
    try {
      const response = await unwrap<ApiResponse<StaffSession>>(
        http.get('/v1/admin/auth/me', { withCredentials: true })
      )
      return response.data ?? null
    } catch (error: unknown) {
      // 401 means no valid session - not an error, just not authenticated
      if (isAxiosError(error) && error.response?.status === 401) {
        return null
      }
      throw error
    }
  }

  /**
   * Exchange bootstrap token for a cookie-backed session
   * POST /v1/admin/auth/token-exchange
   */
  async function exchangeToken(token: string): Promise<StaffSession | null> {
    try {
      const response = await unwrap<ApiResponse<StaffSession>>(
        http.post('/v1/admin/auth/token-exchange', { token }, { withCredentials: true })
      )
      return response.data ?? null
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 401) {
        const responseErrorCode = extractApiErrorCode(error)
        throw { code: responseErrorCode ?? 'TOKEN_EXPIRED', status: 401 }
      }
      throw error
    }
  }

  /**
   * Check if current session is valid
   * GET /v1/admin/auth/check
   */
  async function checkSession(): Promise<SessionCheck> {
    try {
      const response = await unwrap<ApiResponse<SessionCheck>>(
        http.get('/v1/admin/auth/check', { withCredentials: true })
      )
      return response.data ?? { valid: false, slug: null }
    } catch (error: unknown) {
      // On any error, return invalid session
      return { valid: false, slug: null }
    }
  }

  /**
   * Logout current staff session
   * POST /v1/admin/auth/logout
   */
  async function logout(): Promise<void> {
    try {
      await http.post('/v1/admin/auth/logout', {}, { withCredentials: true })
    } catch (error: unknown) {
      // Best effort - continue even if API call fails
      console.warn('Staff logout API call failed:', error)
    }
  }

  /**
   * Create a dev staff session (dev tools only)
   * POST /v1/admin/auth/dev/create-session
   */
  async function createDevSession(config: {
    slug: string
    username: string
    fullName?: string
    userId?: string
    role: string
    permissions?: string[]
  }): Promise<StaffSession | null> {
    const response = await unwrap<ApiResponse<StaffSession>>(
      http.post('/v1/admin/auth/dev/create-session', config, { withCredentials: true })
    )
    return response.data ?? null
  }

  return {
    getSession,
    exchangeToken,
    checkSession,
    logout,
    createDevSession
  }
}

/**
 * Type guard for axios errors
 */
function isAxiosError(error: unknown): error is { response?: { status?: number } } {
  return typeof error === 'object' && error !== null && 'response' in error
}

function extractApiErrorCode(error: unknown): string | null {
  if (!isAxiosError(error) || !error.response || !('data' in error.response)) {
    return null
  }

  const response = error.response as { data?: { error?: { errorCode?: string } } }
  const code = response.data?.error?.errorCode
  return typeof code === 'string' ? code : null
}

export default useStaffApi
